import { NgClass } from '@angular/common'
import { Component, computed, effect, inject, input, output, signal } from '@angular/core'
import { toSignal } from '@angular/core/rxjs-interop'
import { FormControl, FormGroup } from '@angular/forms'
import { AuthService } from '@app/modules/auth/services/auth.service'
import { WorkspaceRoleService } from '@app/modules/auth/services/workspace-role.service'
import { EventPowerUpId } from '@app/v2/event/enums/event-power-up-id.enum'
import { Persona, PersonaClonedPayload } from '@app/v2/personas/types/persona.type'
import { DropdownOption } from '@app/v2/shared/components/dropdown/types/dropdown-option.type'
import { EventCardCoreInput, EventCardPersonaAction, EventCardPersonasInput, EventWrapperModel } from '../../types/event-wrapper-model.type'
import { Event } from '../../types/event.type'
import { EventCardComponent } from '../event-card/event-card.component'
import { EventDatesStatusComponent } from '../event-dates-status/event-dates-status.component'
import { EventHeaderComponent } from '../event-header/event-header.component'
import { EventPowerUpsComponent } from '../event-power-ups/event-power-ups.component'

@Component({
  selector: 'app-event-wrapper',
  standalone: true,
  imports: [NgClass, EventCardComponent, EventPowerUpsComponent, EventHeaderComponent, EventDatesStatusComponent],
  templateUrl: './event-wrapper.component.html',
  styleUrl: './event-wrapper.component.scss'
})
export class EventWrapperComponent {
  public readonly model = input.required<EventWrapperModel>()
  public readonly event = computed(() => this.model().event)
  public readonly isLast = computed(() => this.model().isLast)
  public readonly canEdit = computed(() => {
    this._userRole()
    return this._workspaceRoleService.canEditEvent()
  })

  public readonly canCreate = computed(() => {
    this._userRole()
    return this._workspaceRoleService.canCreateEvent()
  })

  public readonly canModifyEventOrPersonas = computed(() => {
    this._userRole()
    return this._workspaceRoleService.canModifyEventOrPersonas()
  })
  public readonly isInteractionReadonly = computed(() => this.model().card.archived || !this.canModifyEventOrPersonas())
  public readonly headerCloning = computed(() => this.model().header.cloning)
  public readonly headerCloningMessage = computed(() => this.model().header.cloningMessage)
  public readonly eventHasPersonas = computed(() => {
    const total = this.model().card.personasTotal
    if (total !== null) return total > 0
    return this.model().card.personas.length > 0
  })

  public readonly bannerForm = new FormGroup({
    cover_page: new FormControl<string>('', { nonNullable: true })
  })

  public readonly cardCore = computed<EventCardCoreInput>(() => {
    this._userRole()
    return {
      event: this.model().event,
      events: this.model().card.events,
      archived: this.model().card.archived,
      canEdit: this._workspaceRoleService.canEditEvent(),
      canCreate: this._workspaceRoleService.canCreateEvent(),
      loadingCounts: this.model().card.loadingCounts,
      isLoading: this.isLoading() || this.model().card.bannerSaveInProgress,
      timelineBannerLayout: this.model().card.timelineBannerLayout,
      eventForm: this.bannerForm,
      canModifyEventOrPersonas: this._workspaceRoleService.canModifyEventOrPersonas()
    }
  })

  public readonly cardPersonas = computed<EventCardPersonasInput>(() => ({
    personas: this.model().card.personas,
    personasTotal: this.model().card.personasTotal,
    personasLoading: this.model().card.personasLoading,
    createPersonaPending: this.model().card.createPersonaPending,
    createPersonaApiError: this.model().card.createPersonaApiError
  }))

  public readonly bannerChanged = output<{ field: string; value: string; original_image?: string; event: Event }>()
  public readonly eventsChanged = output<Event[]>()
  public readonly modalStateChanged = output<boolean>()
  public readonly archiveUpdateChanged = output<boolean>()
  public readonly cloneConfirmed = output<Event>()
  public readonly archiveConfirmed = output<Event>()
  public readonly personaAction = output<EventCardPersonaAction>()
  public readonly createPersonaSubmitted = output<{ event: Event; name: string }>()
  public readonly personaNameUpdate = output<{ event: Event; persona: Persona }>()
  public readonly personaLandingPageClick = output<{ event: Event; persona: Persona }>()
  public readonly personaViewRegistrantsClick = output<{ event: Event; persona: Persona }>()
  public readonly personaCloned = output<PersonaClonedPayload & { event: Event }>()
  public readonly personaArchiveClick = output<{ event: Event; persona: Persona; personaIdsToArchive: number[] }>()
  public readonly legacyPersonaEditorClosed = output<{ event: Event }>()
  public readonly personasListingWizardClosed = output<{ event: Event }>()
  public readonly restoreConfirmed = output<Event>()
  public readonly powerUpClicked = output<{ event: Event; powerUp: EventPowerUpId }>()
  public isLoading = signal(false)

  public readonly headerDropdownOptions = computed((): DropdownOption[] => {
    this._userRole()
    this._currentWorkspace()
    if (this.model().card.archived) return this._archivedDropdownOptions()
    if (!this.canModifyEventOrPersonas()) return []
    const options: DropdownOption[] = []
    if (this._workspaceRoleService.canCloneEvent()) options.push(this._cloneOption)
    if (this._workspaceRoleService.canOpenArchivedItems()) options.push(this._archivedItemsOption)
    if (this._workspaceRoleService.canAccessApiKeys()) options.push(this._eventIdentifiersOption())
    if (this._workspaceRoleService.canArchiveEvent()) options.push(this._archiveOption)
    return options
  })

  public readonly cardDropdownOptions = computed((): DropdownOption[] => {
    this._userRole()
    this._currentWorkspace()
    if (this.model().card.archived) return this._archivedDropdownOptions()
    if (!this.canModifyEventOrPersonas()) return []
    const options: DropdownOption[] = []
    if (this.canEdit()) options.push(this._editOption)
    if (this._workspaceRoleService.canCloneEvent()) options.push(this._cloneOption)
    if (this._workspaceRoleService.canAccessApiKeys()) options.push(this._eventIdentifiersOption())
    if (this._workspaceRoleService.canArchiveEvent()) options.push(this._archiveOption)
    return options
  })

  private readonly _restoreOption: DropdownOption = {
    key: 'restore',
    value: 'Restore',
    prefixIcon: `<svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg"><g clip-path="url(#clip0_43898_352475)"><path d="M2.05025 2.05025C1.07129 3.02922 0.404602 4.2765 0.134506 5.63437C-0.13559 6.99224 0.00303356 8.3997 0.532848 9.67879C1.06266 10.9579 1.95987 12.0511 3.11101 12.8203C4.26216 13.5895 5.61554 14 7.00001 14C8.38448 14 9.73785 13.5895 10.889 12.8203C12.0401 12.0511 12.9373 10.9579 13.4672 9.67878C13.997 8.39969 14.1356 6.99223 13.8655 5.63436C13.5954 4.27649 12.9287 3.02921 11.9497 2.05025C10.637 0.737493 8.85651 -1.75034e-06 6.99999 0C5.14348 1.75035e-06 3.36301 0.737501 2.05025 2.05025ZM10.9965 6.7335C10.3175 6.05451 9.39658 5.67306 8.43634 5.67306C7.4761 5.67306 6.55518 6.05452 5.87618 6.73351L7.4167 8.27402L2.93388 8.27403V3.79118L4.47436 5.33169C5.08777 4.71821 5.86947 4.30062 6.72043 4.13181C7.57139 3.963 8.4533 4.05057 9.25445 4.38343C10.0556 4.71629 10.7399 5.27947 11.2207 6.0016C11.7015 6.72373 11.9571 7.57232 11.9552 8.43986C11.7992 7.79352 11.4674 7.20295 10.9965 6.7335Z" fill="#1C64F2"/></g><defs><clipPath id="clip0_43898_352475"><rect width="14" height="14" fill="white"/></clipPath></defs></svg>`
  }

  private readonly _deletePermanentlyOption: DropdownOption = {
    key: 'delete-permanently',
    value: 'Delete permanently',
    textColorClass: 'tw-text-red-600 dark:tw-text-red-500 tw-whitespace-nowrap',
    prefixIcon: `<svg width="13" height="14" viewBox="0 0 13 14" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M12.1204 2.94737H9.26852V1.47368C9.26852 1.08284 9.11829 0.708001 8.85087 0.431632C8.58346 0.155263 8.22077 0 7.84259 0H4.99074C4.61256 0 4.24987 0.155263 3.98246 0.431632C3.71505 0.708001 3.56481 1.08284 3.56481 1.47368V2.94737H0.712963C0.523873 2.94737 0.342528 3.025 0.208822 3.16318C0.0751155 3.30137 0 3.48879 0 3.68421C0 3.87963 0.0751155 4.06705 0.208822 4.20524C0.342528 4.34342 0.523873 4.42105 0.712963 4.42105H1.42593V12.5263C1.42593 12.9172 1.57616 13.292 1.84357 13.5684C2.11098 13.8447 2.47367 14 2.85185 14H9.98148C10.3597 14 10.7224 13.8447 10.9898 13.5684C11.2572 13.292 11.4074 12.9172 11.4074 12.5263V4.42105H12.1204C12.3095 4.42105 12.4908 4.34342 12.6245 4.20524C12.7582 4.06705 12.8333 3.87963 12.8333 3.68421C12.8333 3.48879 12.7582 3.30137 12.6245 3.16318C12.4908 3.025 12.3095 2.94737 12.1204 2.94737ZM4.99074 1.47368H7.84259V2.94737H4.99074V1.47368ZM5.7037 11.0526C5.7037 11.2481 5.62859 11.4355 5.49488 11.5737C5.36118 11.7118 5.17983 11.7895 4.99074 11.7895C4.80165 11.7895 4.62031 11.7118 4.4866 11.5737C4.35289 11.4355 4.27778 11.2481 4.27778 11.0526V5.89474C4.27778 5.69931 4.35289 5.5119 4.4866 5.37371C4.62031 5.23553 4.80165 5.15789 4.99074 5.15789C5.17983 5.15789 5.36118 5.23553 5.49488 5.37371C5.62859 5.5119 5.7037 5.69931 5.7037 5.89474V11.0526ZM8.55556 11.0526C8.55556 11.2481 8.48044 11.4355 8.34673 11.5737C8.21303 11.7118 8.03168 11.7895 7.84259 11.7895C7.6535 11.7895 7.47216 11.7118 7.33845 11.5737C7.20475 11.4355 7.12963 11.2481 7.12963 11.0526V5.89474C7.12963 5.69931 7.20475 5.5119 7.33845 5.37371C7.47216 5.23553 7.6535 5.15789 7.84259 5.15789C8.03168 5.15789 8.21303 5.23553 8.34673 5.37371C8.48044 5.5119 8.55556 5.69931 8.55556 5.89474V11.0526Z" fill="#E02424"/></svg>`
  }

  private readonly _cloneOption: DropdownOption = {
    key: 'clone',
    value: 'Clone',
    prefixIcon: `<svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M11.5422 6.70971e-06H8.05033C7.78761 -0.000684373 7.52736 0.0520158 7.28465 0.155056C7.04194 0.258097 6.8216 0.409432 6.63637 0.600299L6.16504 1.08176C6.09114 1.16196 6.03758 1.25951 6.00905 1.36586H8.87565C9.57682 1.37253 10.2468 1.66392 10.7385 2.17606C11.2301 2.6882 11.5034 3.37926 11.4982 4.09757V10.9268H11.5422C11.8899 10.9204 12.2208 10.773 12.4625 10.5169C12.7042 10.2609 12.8369 9.91711 12.8315 9.56098V1.36586C12.8369 1.00973 12.7042 0.665964 12.4625 0.409925C12.2208 0.153886 11.8899 0.00647319 11.5422 6.70971e-06Z" fill="#1C64F2"/><path d="M4.83174 6.14633V2.7317C4.83262 2.63217 4.85541 2.53413 4.8984 2.44487C4.4573 2.53916 4.05272 2.76348 3.73443 3.09023L1.84848 5.02223C1.54789 5.33217 1.33678 5.72119 1.23849 6.14633H4.83174Z" fill="#1C64F2"/><path d="M8.87564 2.39024H6.06304C6.1267 2.49246 6.16196 2.61051 6.16504 2.73171V6.14634C6.16504 6.50859 6.02457 6.856 5.77453 7.11215C5.52448 7.36829 5.18535 7.5122 4.83174 7.5122H1.16516V12.2927C1.15982 12.7393 1.32757 13.1698 1.63162 13.4898C1.93567 13.8099 2.35121 13.9933 2.78712 14H8.87564C9.31161 13.9933 9.72722 13.8099 10.0314 13.4899C10.3355 13.1698 10.5034 12.7393 10.4983 12.2927V4.09756C10.5034 3.65093 10.3355 3.22042 10.0314 2.90038C9.72722 2.58035 9.31161 2.3969 8.87564 2.39024Z" fill="#1C64F2"/></svg>`
  }

  private readonly _archiveOption: DropdownOption = {
    key: 'archive',
    value: 'Archive event',
    textColorClass: 'tw-text-red-600',
    prefixIcon: `<svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M13.2222 0.582031H0.777778C0.348223 0.582031 0 0.920012 0 1.33693V2.84674C0 3.26366 0.348223 3.60164 0.777778 3.60164H13.2222C13.6518 3.60164 14 3.26366 14 2.84674V1.33693C14 0.920012 13.6518 0.582031 13.2222 0.582031Z" fill="#E02424"/><path d="M0.777778 5.11144V11.9056C0.777778 12.306 0.941666 12.69 1.23339 12.9732C1.52511 13.2563 1.92077 13.4154 2.33333 13.4154H11.6667C12.0792 13.4154 12.4749 13.2563 12.7666 12.9732C13.0583 12.69 13.2222 12.306 13.2222 11.9056V5.11144H0.777778ZM9.33333 7.37615C9.33333 7.57636 9.25139 7.76837 9.10553 7.90995C8.95967 8.05152 8.76184 8.13105 8.55556 8.13105H5.44444C5.23817 8.13105 5.04033 8.05152 4.89447 7.90995C4.74861 7.76837 4.66667 7.57636 4.66667 7.37615V6.62125C4.66667 6.42103 4.74861 6.22902 4.89447 6.08745C5.04033 5.94588 5.23817 5.86635 5.44444 5.86635C5.65072 5.86635 5.84855 5.94588 5.99442 6.08745C6.14028 6.22902 6.22222 6.42103 6.22222 6.62125H7.77778C7.77778 6.42103 7.85972 6.22902 8.00558 6.08745C8.15145 5.94588 8.34928 5.86635 8.55556 5.86635C8.76184 5.86635 8.95967 5.94588 9.10553 6.08745C9.25139 6.22902 9.33333 6.42103 9.33333 6.62125V7.37615Z" fill="#E02424"/></svg>`
  }

  private readonly _editOption: DropdownOption = {
    key: 'edit',
    value: 'Edit event',
    prefixIcon: `<svg width="14" height="14" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M9.33872 0.465904L8.38084 1.47763L10.586 3.80671L11.5432 2.79499C11.688 2.64207 11.8029 2.46052 11.8813 2.26071C11.9597 2.06089 12 1.84673 12 1.63045C12 1.41416 11.9597 1.2 11.8813 1.00018C11.8029 0.800369 11.688 0.61882 11.5432 0.465904C11.2461 0.166831 10.8514 0 10.441 0C10.0305 0 9.63584 0.166831 9.33872 0.465904Z" fill="#1C64F2"/><path d="M9.70416 4.73808L7.49904 2.409L4.55243 5.51862C4.46525 5.61052 4.4058 5.72764 4.38156 5.8552L4.05353 7.60201C4.03335 7.70838 4.03836 7.81836 4.0681 7.92219C4.09784 8.02602 4.15141 8.12049 4.22403 8.19719C4.29666 8.2739 4.38609 8.33047 4.4844 8.36189C4.5827 8.3933 4.68683 8.39859 4.78754 8.37727L6.44138 8.02817C6.56192 8.00245 6.67258 7.93966 6.75943 7.8477L9.70416 4.73808Z" fill="#1C64F2"/><path d="M4.55243 5.51862C4.46525 5.61052 4.4058 5.72764 4.38156 5.8552L4.05353 7.60201C4.03335 7.70838 4.03836 7.81836 4.0681 7.92219C4.09784 8.02602 4.15141 8.12049 4.22403 8.19719C4.29666 8.2739 4.38609 8.33047 4.4844 8.36189C4.5827 8.3933 4.68683 8.39859 4.78754 8.37727L6.44138 8.02817C6.56192 8.00245 6.67258 7.93966 6.75943 7.8477L9.70416 4.73808Z" fill="#1C64F2"/><path d="M7.35436 8.61703C7.18011 8.80076 6.9584 8.92609 6.71702 8.97733L4.51189 9.44302C4.31059 9.48548 4.10248 9.47482 3.90602 9.41196C3.70955 9.3491 3.53082 9.236 3.38566 9.08269C3.24051 8.92938 3.13343 8.74059 3.07392 8.53309C3.01441 8.32558 3.00431 8.10577 3.04452 7.89315L3.48542 5.56407C3.53393 5.30911 3.65259 5.07494 3.82654 4.8909L6.89475 1.65021C6.87768 1.5888 6.86887 1.52517 6.86856 1.46117H1.35139C0.993077 1.46152 0.649539 1.61201 0.396176 1.87962C0.142814 2.14722 0.000330238 2.51007 0 2.88852V10.5726C0.000330238 10.9511 0.142814 11.3139 0.396176 11.5816C0.649539 11.8492 0.993077 11.9997 1.35139 12H8.62654C8.98485 11.9997 9.32839 11.8492 9.58175 11.5816C9.83512 11.3139 9.9776 10.9511 9.97793 10.5726V5.84598L7.35436 8.61703Z" fill="#1C64F2"/></svg>`
  }

  private readonly _archivedItemsOption: DropdownOption = {
    key: 'archived-items',
    value: 'Archived items',
    prefixIcon: `<svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M1.86927 13.3002H11.0551L14 6.62961H3.68111L1.19636 12.2581C1.14656 12.371 1.12556 12.4945 1.13526 12.6176C1.14496 12.7407 1.18506 12.8594 1.25191 12.963C1.31877 13.0665 1.41026 13.1516 1.51807 13.2105C1.62589 13.2694 1.74661 13.3002 1.86927 13.3002Z" fill="#1C64F2"/><path d="M2.3353 6.02777C2.45096 5.76566 2.63968 5.54296 2.87855 5.38668C3.11743 5.23039 3.39622 5.14722 3.68111 5.14725H13.252C13.252 4.75411 13.0969 4.37707 12.8207 4.09907C12.5446 3.82108 12.1701 3.6649 11.7796 3.6649H8.42459L6.65766 1.29314C6.52015 1.10942 6.34223 0.960268 6.13785 0.857392C5.93348 0.754516 5.7082 0.70071 5.4797 0.700195H1.47244C1.08193 0.700195 0.707406 0.856371 0.431269 1.13437C0.155132 1.41236 0 1.7894 0 2.18255V11.3183L2.3353 6.02777Z" fill="#1C64F2"/></svg>`
  }

  private readonly _archivedDropdownOptions = computed((): DropdownOption[] => {
    this._userRole()
    const options: DropdownOption[] = []
    if (this._workspaceRoleService.canAccessApiKeys()) options.push(this._eventIdentifiersOption())
    if (this._workspaceRoleService.canRestoreEvent()) options.push(this._restoreOption)
    if (this._workspaceRoleService.canDeleteArchivedEvents()) options.push(this._deletePermanentlyOption)
    return options
  })

  private readonly _workspaceRoleService = inject(WorkspaceRoleService)
  private readonly _authService = inject(AuthService)
  private readonly _userRole = toSignal(this._workspaceRoleService.getUserRole(), { initialValue: null })
  private readonly _currentWorkspace = toSignal(this._authService.currentWorkspaceChange, { initialValue: this._authService.currentWorkspace })

  /**
   * Menu row that downloads event and persona identifiers.
   * @returns {DropdownOption}
   */
  private _eventIdentifiersOption(): DropdownOption {
    return {
      key: 'event-identifiers',
      value: "Get event's identifiers",
      prefixIcon: `<svg width="14" height="14" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
<g clip-path="url(#clip0_12_1561)">
<path d="M14.707 7.48C14.5195 7.28772 14.2652 7.17971 14 7.17971C13.7348 7.17971 13.4805 7.28772 13.293 7.48L11 9.8318V1.02564C11 0.753624 10.8946 0.492748 10.7071 0.300403C10.5196 0.108058 10.2652 0 10 0C9.73478 0 9.48043 0.108058 9.29289 0.300403C9.10536 0.492748 9 0.753624 9 1.02564V9.8318L6.707 7.48C6.61475 7.38204 6.50441 7.30391 6.3824 7.25015C6.2604 7.1964 6.12918 7.16811 5.9964 7.16692C5.86362 7.16574 5.73194 7.19169 5.60905 7.24326C5.48615 7.29483 5.3745 7.37099 5.2806 7.46729C5.18671 7.56359 5.11246 7.6781 5.06218 7.80415C5.0119 7.9302 4.9866 8.06525 4.98775 8.20144C4.9889 8.33762 5.01649 8.47221 5.0689 8.59734C5.12131 8.72247 5.19749 8.83564 5.293 8.93026L9.293 13.0328C9.38589 13.1283 9.49624 13.2041 9.61773 13.2558C9.73922 13.3075 9.86947 13.3341 10.001 13.3341C10.1325 13.3341 10.2628 13.3075 10.3843 13.2558C10.5058 13.2041 10.6161 13.1283 10.709 13.0328L14.709 8.93026C14.8962 8.73765 15.0012 8.47667 15.0008 8.2047C15.0004 7.93274 14.8947 7.67206 14.707 7.48Z" fill="#1C64F2"/>
<path d="M18 11.7949H15.45L12.475 14.8462C12.15 15.1795 11.7641 15.444 11.3395 15.6244C10.9148 15.8048 10.4597 15.8977 10 15.8977C9.54034 15.8977 9.08519 15.8048 8.66053 15.6244C8.23586 15.444 7.85001 15.1795 7.525 14.8462L4.55 11.7949H2C1.46957 11.7949 0.960859 12.011 0.585786 12.3957C0.210714 12.7804 0 13.3021 0 13.8462V17.9487C0 18.4928 0.210714 19.0145 0.585786 19.3992C0.960859 19.7839 1.46957 20 2 20H18C18.5304 20 19.0391 19.7839 19.4142 19.3992C19.7893 19.0145 20 18.4928 20 17.9487V13.8462C20 13.3021 19.7893 12.7804 19.4142 12.3957C19.0391 12.011 18.5304 11.7949 18 11.7949ZM15.5 17.9487C15.2033 17.9487 14.9133 17.8585 14.6666 17.6894C14.42 17.5204 14.2277 17.2801 14.1142 16.999C14.0007 16.7179 13.9709 16.4086 14.0288 16.1101C14.0867 15.8117 14.2296 15.5376 14.4393 15.3224C14.6491 15.1072 14.9164 14.9607 15.2074 14.9014C15.4983 14.842 15.7999 14.8725 16.074 14.9889C16.3481 15.1053 16.5824 15.3025 16.7472 15.5555C16.912 15.8085 17 16.106 17 16.4103C17 16.8183 16.842 17.2096 16.5607 17.4981C16.2794 17.7866 15.8978 17.9487 15.5 17.9487Z" fill="#1C64F2"/>
</g>
<defs>
<clipPath id="clip0_12_1561">
<rect width="20" height="20" fill="#1C64F2"/>
</clipPath>
</defs>
</svg>`
    }
  }

  constructor() {
    effect(() => {
      // Emit value changes so banner-upload-field stays in sync with the latest backend value.
      this.bannerForm.patchValue({ cover_page: this.event()?.cover_page ?? '' })
    })
  }

  /**
   * Emit the events changed event
   * @param events {Event[]} - The events
   */
  public onEventsChanged(events: Event[]): void {
    this.eventsChanged.emit(events)
  }

  /**
   * Emit the modal state changed event
   * @param isOpen {boolean} - The modal state
   */
  public onModalStateChanged(isOpen: boolean): void {
    this.modalStateChanged.emit(isOpen)
  }

  /**
   * Emit the archive update changed event
   * @param isArchiveUpdate {boolean} - The archive update state
   */
  public onArchiveUpdateChanged(isArchiveUpdate: boolean): void {
    this.archiveUpdateChanged.emit(isArchiveUpdate)
  }

  /**
   * Emit the clone confirmed event
   */
  public onCloneConfirmed(): void {
    this.cloneConfirmed.emit(this.event())
  }

  /**
   * Emit the archive confirmed event
   */
  public onArchiveConfirmed(): void {
    this.archiveConfirmed.emit(this.event())
  }

  /**
   * Emit the restore confirmed event
   */
  public onRestoreConfirmed(): void {
    this.restoreConfirmed.emit(this.event())
  }

  /**
   * Emit the banner changed event
   * @param change { { field: string; value: string; original_image?: string; event: Event } } - The banner change
   */
  public onBannerChanged(change: { field: string; value: string; original_image?: string; event: Event }): void {
    this.bannerChanged.emit(change)
  }

  /**
   * Emits create persona request upward.
   * @param payload {{ event: Event; name: string }} - The current event and persona name.
   */
  public onCreatePersonaSubmitted(payload: { event: Event; name: string }): void {
    this.createPersonaSubmitted.emit(payload)
  }

  /**
   * Emits persona name commit upward.
   * @param payload {{ event: Event; persona: Persona }} - Event and edited persona.
   */
  public onPersonaNameUpdate(payload: { event: Event; persona: Persona }): void {
    this.personaNameUpdate.emit(payload)
  }

  /**
   * Emits persona landing-page click upward.
   * @param payload {{ event: Event; persona: Persona }} - Event and selected persona.
   */
  public onPersonaLandingPageClick(payload: { event: Event; persona: Persona }): void {
    this.personaLandingPageClick.emit(payload)
  }

  /**
   * Emits persona view registrants click upward.
   * @param payload {{ event: Event; persona: Persona }} - Event and selected persona.
   */
  public onPersonaViewRegistrantsClick(payload: { event: Event; persona: Persona }): void {
    this.personaViewRegistrantsClick.emit(payload)
  }

  /**
   * Emits a successfully cloned persona upward.
   * @param payload {PersonaClonedPayload & { event: Event }} - Event, cloned persona, and message.
   */
  public onPersonaCloned(payload: PersonaClonedPayload & { event: Event }): void {
    this.personaCloned.emit(payload)
  }

  /**
   * Emits persona remove click upward.
   * @param payload {{ event: Event; persona: Persona }} - Event and selected persona.
   */
  public onPersonaArchiveClick(payload: { event: Event; persona: Persona; personaIdsToArchive: number[] }): void {
    this.personaArchiveClick.emit(payload)
  }

  // TODO: Remove this once the legacy persona editor is removed
  public onLegacyPersonaEditorClosed(payload: { event: Event }): void {
    this.legacyPersonaEditorClosed.emit(payload)
  }

  /**
   * Forwards personasListingWizardClosed so the timeline can refresh personas.
   * @param payload {{ event: Event }} - Event.
   */
  public onPersonasListingWizardClosed(payload: { event: Event }): void {
    this.personasListingWizardClosed.emit(payload)
  }

  public onPersonaAction(action: EventCardPersonaAction): void {
    this.personaAction.emit(action)
    switch (action.type) {
      case 'createPersonaSubmitted':
        this.onCreatePersonaSubmitted(action.payload)
        return
      case 'personaNameUpdate':
        this.onPersonaNameUpdate(action.payload)
        return
      case 'personaLandingPageClick':
        this.onPersonaLandingPageClick(action.payload)
        return
      case 'personaViewRegistrantsClick':
        this.onPersonaViewRegistrantsClick(action.payload)
        return
      case 'personaCloned':
        this.onPersonaCloned(action.payload)
        return
      case 'personaArchiveClick':
        this.onPersonaArchiveClick(action.payload)
        return
      case 'legacyPersonaEditorClosed':
        this.onLegacyPersonaEditorClosed(action.payload)
        return
      case 'personasListingWizardClosed':
        this.onPersonasListingWizardClosed(action.payload)
        return
      default:
        return
    }
  }

  /**
   * Re-emit a power-up card click with the current event attached.
   * @param powerUp {EventPowerUpId} - The id of the clicked power-up card.
   */
  public onPowerUpClicked(powerUp: EventPowerUpId): void {
    this.powerUpClicked.emit({ event: this.event(), powerUp })
  }
}
